import {
  CREATION_DRAFT_MAX_BYTES, CREATION_DRAFT_TTL_MS,
  creationDraftSchema, type CreationDraft, type CreationDraftInput,
} from "@ppt-agent/contracts";

export const CREATION_DRAFT_PREFIX = "flowisle:creation-draft:";
const MAX_DRAFTS = 8;
const idPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const recovery = "草稿已失效或不在当前标签页，请返回官网重新输入。";

export type CreationDraftResult = { draft?: CreationDraft; error?: string };

function pruneDrafts(storage: Storage, now: number, keepId: string): void {
  const records: { key: string; createdAt: number }[] = [];
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (!key?.startsWith(CREATION_DRAFT_PREFIX)) continue;
    try {
      const raw = storage.getItem(key);
      const record = raw ? creationDraftSchema.safeParse(JSON.parse(raw)) : undefined;
      records.push({ key, createdAt: record?.success ? record.data.createdAt : 0 });
    } catch {
      records.push({ key, createdAt: 0 });
    }
  }
  records.sort((left, right) => right.createdAt - left.createdAt);
  const keepKey = CREATION_DRAFT_PREFIX + keepId;
  let retained = 1;
  for (const record of records) {
    if (record.key === keepKey) continue;
    if (now - record.createdAt >= CREATION_DRAFT_TTL_MS || retained >= MAX_DRAFTS) {
      storage.removeItem(record.key);
    } else {
      retained++;
    }
  }
}

export function saveCreationDraft(input: CreationDraftInput): string {
  const draft = creationDraftSchema.safeParse({
    version: 1, id: globalThis.crypto.randomUUID(), createdAt: Date.now(), input,
  });
  if (!draft.success) throw new Error("草稿格式无效，请检查输入后重试。");
  const serialized = JSON.stringify(draft.data);
  if (new TextEncoder().encode(serialized).byteLength > CREATION_DRAFT_MAX_BYTES) {
    throw new Error("内容过长，请将草稿精简至 200 KB 以内后重试；原文仍保留在输入框中。");
  }
  let storage: Storage;
  try {
    storage = globalThis.sessionStorage;
    storage.setItem(CREATION_DRAFT_PREFIX + draft.data.id, serialized);
  } catch {
    throw new Error("浏览器无法暂存草稿，请允许本站使用会话存储后重试；原文仍保留在输入框中。");
  }
  // Cleanup is best-effort and never removes unrelated storage or the new draft.
  try { pruneDrafts(storage, draft.data.createdAt, draft.data.id); } catch { /* Saved draft remains usable. */ }
  return draft.data.id;
}

export function readCreationDraft(search: string): CreationDraftResult {
  const params = new URLSearchParams(search);
  if (!params.has("draft")) return {};
  const id = params.get("draft") ?? "";
  if (!idPattern.test(id)) return { error: recovery };
  try {
    const raw = globalThis.sessionStorage.getItem(CREATION_DRAFT_PREFIX + id);
    if (!raw || new TextEncoder().encode(raw).byteLength > CREATION_DRAFT_MAX_BYTES) return { error: recovery };
    const result = creationDraftSchema.safeParse(JSON.parse(raw));
    if (!result.success || result.data.id !== id || result.data.createdAt > Date.now()
      || Date.now() - result.data.createdAt >= CREATION_DRAFT_TTL_MS) return { error: recovery };
    return { draft: result.data };
  } catch {
    return { error: recovery };
  }
}
